import { useMemo } from "react";
import { Task, TaskInstance, InstanceStatus, RecurrencePattern } from "@/lib/api/checklist";
import { getPeriodsForRecurrence, groupInstancesByPeriod, getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { Check, Clock, AlertCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";

interface MonthlyWeeklyBreakdownViewProps {
  tasks: Task[];
  instances: TaskInstance[];
  lookbackPeriods: number;
  pattern: RecurrencePattern.MONTHLY | RecurrencePattern.WEEKLY;
}

export function MonthlyWeeklyBreakdownView({ tasks, instances, lookbackPeriods, pattern }: MonthlyWeeklyBreakdownViewProps) {
  // Get date range and periods
  const { start, end } = getDateRangeForRecurrence(pattern, lookbackPeriods);
  const periods = getPeriodsForRecurrence(pattern, start, end);
  
  // Format periods for display and group by month/year
  const { displayPeriods, groupings } = useMemo(() => {
    const periods_ = [...periods].reverse();
    
    if (pattern === RecurrencePattern.WEEKLY) {
      // Group weeks by month
      const monthGroups = new Map<string, number>();
      let currentMonth = '';
      let count = 0;
      
      const formatted = periods_.map(period => {
        const monthKey = moment(period.start).format('YYYY-MM');
        const monthLabel = moment(period.start).format('MMM YYYY');
        
        if (monthKey !== currentMonth) {
          if (currentMonth) {
            monthGroups.set(currentMonth, count);
          }
          currentMonth = monthKey;
          count = 0;
        }
        count++;

        return {
          label: `W${moment(period.start).isoWeek()}`,
          periodKey: period.key,
          monthKey,
          monthLabel,
        };
      });
      
      if (currentMonth) {
        monthGroups.set(currentMonth, count);
      }
      
      return { 
        displayPeriods: formatted,
        groupings: Array.from(monthGroups.entries()).map(([key, count]) => ({
          key,
          label: moment(key, 'YYYY-MM').format('MMM YYYY'),
          count
        }))
      };
    } else {
      // Group months by year
      const yearGroups = new Map<number, number>();
      let currentYear = 0;
      let count = 0;
      
      const formatted = periods_.map(period => {
        const year = moment(period.start).year();
        
        if (year !== currentYear) {
          if (currentYear) {
            yearGroups.set(currentYear, count);
          }
          currentYear = year;
          count = 0;
        }
        count++;
        
        return {
          label: period.label,
          periodKey: period.key,
          year,
        };
      });
      
      if (currentYear) {
        yearGroups.set(currentYear, count);
      }
      
      return {
        displayPeriods: formatted,
        groupings: Array.from(yearGroups.entries()).map(([key, count]) => ({
          key: key.toString(),
          label: key.toString(),
          count
        }))
      };
    }
  }, [periods, pattern]);
  
  // Group instances by task and period
  const instancesByTaskAndPeriod = useMemo(() => {
    const grouped = new Map<string, Map<string, TaskInstance[]>>();
    
    tasks.forEach(task => {
      const taskInstances = instances.filter(inst => inst.task_id === task.id);
      const periodMap = groupInstancesByPeriod(taskInstances, periods);
      grouped.set(task.id, periodMap);
    });
    
    return grouped;
  }, [tasks, instances, periods]);
  
  // Get task deadline from first instance
  const getTaskDeadline = (task: Task): string => {
    const taskInstances = instances.filter(inst => inst.task_id === task.id);
    if (taskInstances.length > 0) {
      const firstInstance = taskInstances.sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      )[0];
      
      if (pattern === RecurrencePattern.WEEKLY) {
        return moment.tz(firstInstance.due_date, CommonDestinationTimezone).format("dddd");
      } else {
        return moment.tz(firstInstance.due_date, CommonDestinationTimezone).format("Do");
      }
    }
    return "";
  };
  
  // Get status for a task's period
  const getPeriodStatus = (task: Task, periodKey: string) => {
    const taskPeriodMap = instancesByTaskAndPeriod.get(task.id);
    const periodInstances = taskPeriodMap?.get(periodKey) || [];
    
    if (periodInstances.length === 0) {
      const period = periods.find(p => p.key === periodKey);
      if (period && period.start > new Date()) {
        return { status: 'future', label: '', icon: null };
      }
      return { status: 'no-data', label: '', icon: null };
    }
    
    const completed = periodInstances.find(inst => inst.status === InstanceStatus.COMPLETED);
    const pending = periodInstances.find(inst => inst.status === InstanceStatus.PENDING);
    
    if (completed) {
      return {
        status: completed.is_late ? 'completed-late' : 'completed',
        label: '',
        icon: completed.is_late ? <Check className="h-2 w-2 text-orange-700" /> : <Check className="h-2 w-2 text-green-700" />,
      };
    }
    
    if (pending) {
      return {
        status: pending.is_late ? 'late' : 'pending',
        label: '',
        icon: pending.is_late ? <AlertCircle className="h-2 w-2 text-red-700" /> : <Clock className="h-2 w-2 text-blue-700" />,
      };
    }
    
    return { status: 'unknown', label: '', icon: null };
  };
  
  // Sort tasks: those with data first, then those without
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aInstances = instances.filter(inst => inst.task_id === a.id);
      const bInstances = instances.filter(inst => inst.task_id === b.id);
      
      // Tasks with instances come first
      if (aInstances.length > 0 && bInstances.length === 0) return -1;
      if (aInstances.length === 0 && bInstances.length > 0) return 1;
      
      // If both have instances or both don't, maintain original order
      return 0;
    });
  }, [tasks, instances]);
  
  return (
    <div className="space-y-1">
      {/* Header with group labels */}
      <div className="mb-1">
        <div className="flex gap-0.5">
          <div className="w-[240px] flex-shrink-0" />
          {groupings.map((group, index) => (
            <div 
              key={group.key} 
              className="text-center"
              style={{ width: `${group.count * 32 + (group.count - 1) * 2}px` }}
            >
              <div className="text-xs font-medium text-muted-foreground">
                <b>{group.label}</b>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Period labels */}
      <div className="flex gap-0.5 overflow-x-auto">
        <div className="w-[240px] flex-shrink-0" />
        <div className="flex gap-0.5">
          {displayPeriods.map((period) => (
            <div key={period.periodKey} className="w-8 text-[10px] text-center font-medium bg-yellow-100 text-yellow-800 rounded-sm">
              {period.label}
            </div>
          ))}
        </div>
      </div>
      
      {/* Colored borders under groups */}
      <div className="flex gap-0.5 -mt-1">
        <div className="w-[240px] flex-shrink-0" />
        {groupings.map((group, index) => (
          <div 
            key={group.key}
            className={cn(
              "h-1 rounded-full",
              index % 2 === 0 ? "bg-purple-400" : "bg-teal-400"
            )}
            style={{ width: `${group.count * 32 + (group.count - 1) * 2}px` }}
          />
        ))}
      </div>
      
      {/* Tasks */}
      <div className="overflow-x-auto">
        {sortedTasks.map(task => {
          const deadline = getTaskDeadline(task);
          
          return (
            <div key={task.id} className="flex gap-0.5 items-center group hover:bg-muted/50 rounded-lg p-1 -m-1">
              {/* Task Info */}
              <div className="w-[240px] pr-2 flex-shrink-0">
                <div className="text-sm font-medium truncate">{task.title}</div>
                {deadline && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {pattern === RecurrencePattern.WEEKLY ? `Every ${deadline}` : `${deadline} of month`}
                  </div>
                )}
              </div>
              
              {/* Periods */}
              <div className="flex gap-0.5">
                {displayPeriods.map(period => {
                  const { status, icon } = getPeriodStatus(task, period.periodKey);
                  const periodData = periods.find(p => p.key === period.periodKey);
                  const isFuture = periodData && periodData.start > new Date();
                  const hasData = status !== 'no-data' && status !== 'future';
                  
                  return (
                    <div
                      key={period.periodKey}
                      className={cn(
                        "w-8 h-6 rounded flex items-center justify-center transition-all",
                        "border",
                        // Border styles
                        hasData && !isFuture && "border-indigo-400",
                        !hasData && !isFuture && "border-gray-200",
                        isFuture && "border-transparent opacity-30",
                        // Background styles
                        status === 'completed' && "bg-green-50",
                        status === 'completed-late' && "bg-orange-50",
                        status === 'pending' && "bg-blue-50",
                        status === 'late' && "bg-red-50",
                        status === 'no-data' && !isFuture && "bg-white",
                        isFuture && "bg-gray-50"
                      )}
                    >
                      {icon}
                      {(!icon && status === 'no-data' && !isFuture) && (
                        <div className="w-2 h-px bg-gray-300" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Compact Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-4 pt-2 border-t">
        <div className="flex items-center gap-1">
          <Check className="h-2 w-2 text-green-700" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <Check className="h-2 w-2 text-orange-700" />
          <span>Completed Late</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="h-2 w-2 text-blue-700" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <AlertCircle className="h-2 w-2 text-red-700" />
          <span>Late</span>
        </div>
      </div>
    </div>
  );
}
